import React, { useState, useEffect, useRef, useMemo } from 'react';
import { get, post, put, del } from 'aws-amplify/api';
import './VideoManualGenerator.css';

const MAX_BYTES = 200 * 1024 * 1024;
const POLL_INTERVAL_MS = 4000;
const HISTORY_POLL_INTERVAL_MS = 10000;
const MANUAL_TEXT_MAX = 20000;
// この時間を過ぎても待機中・生成中のままのものは、自動更新の対象にしない（アップロード中断などで止まったもの）
const IN_FLIGHT_STALE_MS = 30 * 60 * 1000;

const STATUS_LABELS = {
  pending: '待機中',
  processing: '生成中…',
  completed: '完了',
  failed: '失敗',
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('ja-JP') : '');

// ファイル名に使えない文字を置き換える
const toSafeFileName = (title) => (title || 'マニュアル').replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 100);

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// 印刷・Word出力用のHTMLを組み立てる
const buildManualHtml = (manual) => `<!DOCTYPE html>
<html lang="ja"><head><meta charset="utf-8"><title>${escapeHtml(manual.title)}</title>
<style>
  body { font-family: 'Hiragino Sans', 'Yu Gothic', 'Meiryo', sans-serif; line-height: 1.8; color: #111827; margin: 40px; }
  h1 { font-size: 20px; border-bottom: 2px solid #3B82F6; padding-bottom: 6px; }
  .meta { color: #6B7280; font-size: 12px; margin-bottom: 20px; }
  .body { white-space: pre-wrap; font-size: 14px; }
</style></head>
<body>
  <h1>${escapeHtml(manual.title || '(無題)')}</h1>
  <div class="meta">作成日時: ${escapeHtml(formatDateTime(manual.created_at))}</div>
  <div class="body">${escapeHtml(manual.manual_text)}</div>
</body></html>`;

const downloadBlob = (content, mimeType, fileName) => {
  // 先頭にBOMを付けて Windows のメモ帳/Word でも文字化けしないようにする
  const blob = new Blob(['﻿', content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Amplify のエラーからサーバーのエラーメッセージを取り出す
const extractErrorMessage = async (e, fallback) => {
  try { const b = await e.response.body.json(); return b.error || fallback; } catch { return fallback; }
};

function VideoManualGenerator() {
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle / uploading / processing / completed / failed
  const [errorMessage, setErrorMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  // 表示中のマニュアル（生成直後・履歴から開いたもの共通）
  const [activeManual, setActiveManual] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailError, setDetailError] = useState('');

  // 履歴の検索・選択
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const pollTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const detailRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    return () => stopPolling();
  }, []);

  // 生成中・待機中のものがある間は履歴を定期更新（画面を離れて戻ってきた場合も状態が反映されるように）
  const hasInFlight = history.some((h) =>
    (h.status === 'pending' || h.status === 'processing') &&
    Date.now() - new Date(h.updated_at || h.created_at).getTime() < IN_FLIGHT_STALE_MS);
  useEffect(() => {
    if (!hasInFlight) return undefined;
    const timer = setInterval(fetchHistory, HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasInFlight]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchHistory = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/video-manuals' });
      const { body } = await op.response;
      const rows = await body.json();
      setHistory(rows);
      // 削除済みのIDは選択状態から外す
      setCheckedIds((prev) => new Set([...prev].filter((id) => rows.some((r) => r.id === id))));
    } catch (e) {
      console.error('履歴取得エラー:', e);
    }
  };

  const fetchManual = async (id) => {
    const op = get({ apiName: 'worktimerApi', path: `/video-manuals/${id}` });
    const { body } = await op.response;
    return body.json();
  };

  const openManual = async (id) => {
    if (isEditing && !window.confirm('編集中の内容は破棄されます。よろしいですか？')) return;
    setIsEditing(false);
    setDetailError('');
    setLoadingDetail(true);
    try {
      const data = await fetchManual(id);
      setActiveManual(data);
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    } catch (e) {
      console.error('マニュアル取得エラー:', e);
      setDetailError('マニュアルの取得に失敗しました');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeManual = () => {
    if (isEditing && !window.confirm('編集中の内容は破棄されます。よろしいですか？')) return;
    setIsEditing(false);
    setActiveManual(null);
    setDetailError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setErrorMessage('動画ファイルを選択してください');
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrorMessage('ファイルサイズが上限(200MB)を超えています');
      return;
    }
    setErrorMessage('');
    setSelectedFile(file);
    // 拡張子を除いたファイル名をタイトルの初期値にする
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const uploadVideo = (uploadUrl, file) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`動画のアップロードに失敗しました（エラーコード: ${xhr.status}）。時間をおいて再度お試しください`));
      };
      xhr.onerror = () => reject(new Error('動画のアップロード中に通信エラーが発生しました。インターネット接続を確認して、再度お試しください'));
      xhr.send(file);
    });
  };

  const pollStatus = (videoManualId) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const data = await fetchManual(videoManualId);
        if (data.status === 'completed') {
          stopPolling();
          setPhase('completed');
          setIsEditing(false);
          setActiveManual(data);
          fetchHistory();
        } else if (data.status === 'failed') {
          stopPolling();
          setErrorMessage(data.error_message || '生成に失敗しました');
          setPhase('failed');
          fetchHistory();
        }
      } catch (e) {
        console.error('ステータス取得エラー:', e);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleStart = async () => {
    if (!selectedFile) {
      setErrorMessage('動画ファイルを選択してください');
      return;
    }
    setErrorMessage('');
    setUploadProgress(0);
    setPhase('uploading');

    let createdId = null;
    try {
      const restOperation = post({
        apiName: 'worktimerApi',
        path: '/video-manuals/presign',
        options: {
          body: {
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            contentType: selectedFile.type,
            title: title || selectedFile.name,
          },
        },
      });
      const { body } = await restOperation.response;
      const { videoManualId, uploadUrl } = await body.json();
      createdId = videoManualId;
      fetchHistory();

      await uploadVideo(uploadUrl, selectedFile);
      createdId = null;

      setPhase('processing');
      pollStatus(videoManualId);
    } catch (e) {
      console.error('アップロードエラー:', e);
      setErrorMessage(e.message || 'アップロードに失敗しました');
      setPhase('failed');
      // アップロードできなかった記録は履歴に残さない
      if (createdId) {
        try {
          await del({ apiName: 'worktimerApi', path: `/video-manuals/${createdId}` }).response;
        } catch (delErr) {
          console.error('未完了レコードの削除に失敗:', delErr);
        }
        fetchHistory();
      }
    }
  };

  const handleCopy = async () => {
    if (!activeManual) return;
    try {
      await navigator.clipboard.writeText(activeManual.manual_text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('コピーエラー:', e);
    }
  };

  const handleReset = () => {
    stopPolling();
    setSelectedFile(null);
    setTitle('');
    setUploadProgress(0);
    setErrorMessage('');
    setPhase('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ===== ダウンロード =====
  const handleDownload = (format) => {
    if (!activeManual) return;
    const base = toSafeFileName(activeManual.title);
    if (format === 'txt') {
      downloadBlob(activeManual.manual_text || '', 'text/plain;charset=utf-8', `${base}.txt`);
    } else if (format === 'md') {
      const md = `# ${activeManual.title || '(無題)'}\n\n作成日時: ${formatDateTime(activeManual.created_at)}\n\n${activeManual.manual_text || ''}\n`;
      downloadBlob(md, 'text/markdown;charset=utf-8', `${base}.md`);
    } else if (format === 'word') {
      // HTML形式の .doc（Word でそのまま開ける）
      downloadBlob(buildManualHtml(activeManual), 'application/msword;charset=utf-8', `${base}.doc`);
    }
  };

  // 印刷ダイアログから「PDFに保存」できる
  const handlePrint = () => {
    if (!activeManual) return;
    const w = window.open('', '_blank');
    if (!w) {
      setDetailError('ポップアップがブロックされました。ブラウザの設定で許可してください');
      return;
    }
    w.document.write(buildManualHtml(activeManual));
    w.document.close();
    w.focus();
    w.onload = () => w.print();
    // onload が発火しないブラウザ向けのフォールバック
    setTimeout(() => { try { w.print(); } catch (_) {} }, 500);
  };

  // ===== 編集 =====
  const startEdit = () => {
    setEditTitle(activeManual.title || '');
    setEditText(activeManual.manual_text || '');
    setDetailError('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDetailError('');
  };

  const saveEdit = async () => {
    if (!editTitle.trim()) {
      setDetailError('タイトルを入力してください');
      return;
    }
    if (editText.length > MANUAL_TEXT_MAX) {
      setDetailError(`本文は${MANUAL_TEXT_MAX}文字以内にしてください`);
      return;
    }
    setSaving(true);
    setDetailError('');
    try {
      const op = put({
        apiName: 'worktimerApi',
        path: `/video-manuals/${activeManual.id}`,
        options: { body: { title: editTitle.trim(), manual_text: editText } },
      });
      const { body } = await op.response;
      const updated = await body.json();
      setActiveManual(updated);
      setIsEditing(false);
      fetchHistory();
    } catch (e) {
      console.error('保存エラー:', e);
      setDetailError(await extractErrorMessage(e, '保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // ===== 削除 =====
  const deleteManuals = async (ids) => {
    setDeleting(true);
    const failed = [];
    for (const id of ids) {
      try {
        const op = del({ apiName: 'worktimerApi', path: `/video-manuals/${id}` });
        await op.response;
      } catch (e) {
        console.error('削除エラー:', id, e);
        failed.push(id);
      }
    }
    setDeleting(false);
    if (activeManual && ids.includes(activeManual.id) && !failed.includes(activeManual.id)) {
      setIsEditing(false);
      setActiveManual(null);
    }
    await fetchHistory();
    if (failed.length > 0) {
      window.alert(`${failed.length}件の削除に失敗しました。時間をおいて再度お試しください。`);
    }
  };

  const handleDeleteOne = async (item) => {
    if (!window.confirm(`「${item.title || '(無題)'}」を削除します。元の動画も削除され、元に戻せません。よろしいですか？`)) return;
    await deleteManuals([item.id]);
  };

  const handleDeleteChecked = async () => {
    const ids = [...checkedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`選択した${ids.length}件のマニュアルを削除します。元の動画も削除され、元に戻せません。よろしいですか？`)) return;
    await deleteManuals(ids);
  };

  // ===== 履歴の絞り込み・選択 =====
  const filteredHistory = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return history.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (q && !(item.title || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [history, searchQuery, statusFilter]);

  const allFilteredChecked = filteredHistory.length > 0 && filteredHistory.every((h) => checkedIds.has(h.id));

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredChecked) filteredHistory.forEach((h) => next.delete(h.id));
      else filteredHistory.forEach((h) => next.add(h.id));
      return next;
    });
  };

  const isBusy = phase === 'uploading' || phase === 'processing';

  return (
    <div className="vmg">
      <div className="vmg-intro">
        <h2>🎬 操作マニュアル生成</h2>
        <p>操作を録画した動画をアップロードすると、AIが箇条書きの操作手順マニュアルを自動生成します。生成したマニュアルは下の履歴からいつでも閲覧・編集・ダウンロード・削除できます。</p>
      </div>

      <div className="vmg-notice" role="note">
        <div className="vmg-notice-title">⚠️ ご注意</div>
        <ul>
          <li>アップロードした<strong>動画は、アップロードから30日後に自動で削除</strong>されます。元の動画が必要な場合は、お手元に保存しておいてください。</li>
          <li>生成されたマニュアル（文章）は自動削除されません。履歴からいつでも閲覧・ダウンロードできます。</li>
          <li>履歴からマニュアルを削除すると、元の動画も同時に削除され、元に戻せません。</li>
        </ul>
      </div>

      <div className="vmg-upload-card">
        <label className="vmg-field-label">タイトル</label>
        <input
          type="text"
          className="vmg-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="例: 見積作成手順"
          maxLength={255}
          disabled={isBusy}
        />

        <label className="vmg-field-label">動画ファイル（200MBまで）</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileChange}
          disabled={isBusy}
        />

        {errorMessage && <div className="vmg-error">{errorMessage}</div>}

        {phase === 'uploading' && (
          <div className="vmg-progress-wrap">
            <div className="vmg-progress-bar">
              <div className="vmg-progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <span>アップロード中… {uploadProgress}%</span>
          </div>
        )}

        {phase === 'processing' && (
          <div className="vmg-status vmg-status-processing">
            <span className="vmg-spinner" />
            AIが動画を解析してマニュアルを生成しています…（数十秒〜数分かかります。この画面を離れても生成は続き、完了後は履歴から確認できます）
          </div>
        )}

        <div className="vmg-actions">
          <button
            className="vmg-btn-primary"
            onClick={handleStart}
            disabled={isBusy || !selectedFile}
          >
            {isBusy ? '処理中…' : 'マニュアルを生成する'}
          </button>
          {(phase === 'completed' || phase === 'failed') && (
            <button className="vmg-btn-secondary" onClick={handleReset}>
              新しい動画を選び直す
            </button>
          )}
        </div>
      </div>

      {(activeManual || loadingDetail) && (
        <div className="vmg-result-card" ref={detailRef}>
          {loadingDetail ? (
            <div className="vmg-status vmg-status-processing"><span className="vmg-spinner" />読み込み中…</div>
          ) : (
            <>
              <div className="vmg-result-header">
                {isEditing ? (
                  <input
                    type="text"
                    className="vmg-title-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    maxLength={255}
                  />
                ) : (
                  <div>
                    <h3>{activeManual.title || '(無題)'}</h3>
                    <div className="vmg-result-meta">
                      作成: {formatDateTime(activeManual.created_at)}
                      {activeManual.updated_at && activeManual.status === 'completed' &&
                        new Date(activeManual.updated_at) - new Date(activeManual.created_at) > 60000 &&
                        ` ／ 更新: ${formatDateTime(activeManual.updated_at)}`}
                    </div>
                  </div>
                )}
                <button className="vmg-btn-icon" onClick={closeManual} title="閉じる" aria-label="閉じる">✕</button>
              </div>

              {detailError && <div className="vmg-error vmg-error-inline">{detailError}</div>}

              {activeManual.status !== 'completed' ? (
                <div className={`vmg-status ${activeManual.status === 'failed' ? 'vmg-status-failed' : 'vmg-status-processing'}`}>
                  {activeManual.status === 'failed'
                    ? `生成に失敗しました: ${activeManual.error_message || '原因不明のエラー'}`
                    : 'まだ生成中です。完了するとここに表示されます。'}
                </div>
              ) : isEditing ? (
                <>
                  <textarea
                    className="vmg-manual-editor"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={Math.min(30, Math.max(10, editText.split('\n').length + 2))}
                  />
                  <div className={`vmg-char-count ${editText.length > MANUAL_TEXT_MAX ? 'over' : ''}`}>
                    {editText.length.toLocaleString()} / {MANUAL_TEXT_MAX.toLocaleString()} 文字
                  </div>
                </>
              ) : (
                <pre className="vmg-manual-text">{activeManual.manual_text}</pre>
              )}

              <div className="vmg-toolbar">
                {isEditing ? (
                  <>
                    <button className="vmg-btn-primary" onClick={saveEdit} disabled={saving}>
                      {saving ? '保存中…' : '保存'}
                    </button>
                    <button className="vmg-btn-secondary" onClick={cancelEdit} disabled={saving}>キャンセル</button>
                  </>
                ) : (
                  <>
                    {activeManual.status === 'completed' && (
                      <>
                        <button className="vmg-btn-secondary" onClick={handleCopy}>
                          {copied ? 'コピーしました' : '📋 コピー'}
                        </button>
                        <button className="vmg-btn-secondary" onClick={startEdit}>✏️ 編集</button>
                        <div className="vmg-download-group">
                          <span className="vmg-download-label">ダウンロード:</span>
                          <button className="vmg-btn-small" onClick={() => handleDownload('txt')}>テキスト</button>
                          <button className="vmg-btn-small" onClick={() => handleDownload('md')}>Markdown</button>
                          <button className="vmg-btn-small" onClick={() => handleDownload('word')}>Word</button>
                          <button className="vmg-btn-small" onClick={handlePrint}>印刷 / PDF</button>
                        </div>
                      </>
                    )}
                    <button
                      className="vmg-btn-danger"
                      onClick={() => handleDeleteOne(activeManual)}
                      disabled={deleting}
                    >
                      🗑 削除
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="vmg-history-card">
        <div className="vmg-history-header">
          <h3>生成履歴 <span className="vmg-history-count">{history.length}件</span></h3>
          {checkedIds.size > 0 && (
            <button className="vmg-btn-danger" onClick={handleDeleteChecked} disabled={deleting}>
              {deleting ? '削除中…' : `選択した${checkedIds.size}件を削除`}
            </button>
          )}
        </div>

        {history.length > 0 && (
          <div className="vmg-history-filters">
            <input
              type="search"
              className="vmg-search-input"
              placeholder="タイトルで検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="vmg-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">すべてのステータス</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        )}

        {history.length === 0 ? (
          <div className="vmg-history-empty">まだ生成履歴がありません</div>
        ) : filteredHistory.length === 0 ? (
          <div className="vmg-history-empty">条件に一致するマニュアルがありません</div>
        ) : (
          <div className="vmg-table-wrap">
            <table className="vmg-history-table">
              <thead>
                <tr>
                  <th className="vmg-col-check">
                    <input
                      type="checkbox"
                      checked={allFilteredChecked}
                      onChange={toggleAllFiltered}
                      aria-label="すべて選択"
                    />
                  </th>
                  <th>タイトル</th>
                  <th>ステータス</th>
                  <th>作成日時</th>
                  <th className="vmg-col-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((item) => (
                  <tr
                    key={item.id}
                    className={`vmg-history-row ${activeManual?.id === item.id ? 'active' : ''}`}
                    onClick={() => openManual(item.id)}
                  >
                    <td className="vmg-col-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checkedIds.has(item.id)}
                        onChange={() => toggleChecked(item.id)}
                        aria-label={`${item.title || '(無題)'} を選択`}
                      />
                    </td>
                    <td className="vmg-col-title">{item.title || '(無題)'}</td>
                    <td>
                      <span className={`vmg-badge vmg-badge-${item.status}`} title={item.error_message || ''}>
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                    </td>
                    <td>{formatDateTime(item.created_at)}</td>
                    <td className="vmg-col-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="vmg-btn-small" onClick={() => openManual(item.id)}>開く</button>
                      <button
                        className="vmg-btn-small vmg-btn-small-danger"
                        onClick={() => handleDeleteOne(item)}
                        disabled={deleting}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoManualGenerator;
